import {
  ArrowDownTrayIcon,
  BoltIcon,
  PlayIcon,
  StopIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React from 'react';
import { toast } from 'sonner';

interface BulkActionsProps {
  selectedItems: Set<string>;
  selectedType: 'matters' | 'tasks' | 'calls';
  onClearSelection: () => void;
  onBulkAction: (action: string, items: string[]) => Promise<void>;
  isLoading?: boolean;
}

const BULK_ACTIONS = {
  matters: [
    {
      key: 'trigger_scheduler',
      label: 'Trigger Scheduler',
      icon: BoltIcon,
      color: 'text-purple-600',
    },
    {
      key: 'bulk_cancel',
      label: 'Cancel Scheduled',
      icon: XMarkIcon,
      color: 'text-red-600',
    },
    {
      key: 'export',
      label: 'Export Data',
      icon: ArrowDownTrayIcon,
      color: 'text-blue-600',
    },
  ],
  tasks: [
    {
      key: 'trigger_now',
      label: 'Trigger Now',
      icon: PlayIcon,
      color: 'text-green-600',
    },
    {
      key: 'cancel_scheduled',
      label: 'Cancel Scheduled',
      icon: XMarkIcon,
      color: 'text-red-600',
    },
    {
      key: 'terminate_active',
      label: 'Terminate Active',
      icon: StopIcon,
      color: 'text-red-600',
    },
  ],
  calls: [
    {
      key: 'export_transcripts',
      label: 'Export Transcripts',
      icon: ArrowDownTrayIcon,
      color: 'text-blue-600',
    },
  ],
} as const;

export const BulkActions: React.FC<BulkActionsProps> = ({
  selectedItems,
  selectedType,
  onClearSelection,
  onBulkAction,
  isLoading = false,
}) => {
  const actions = BULK_ACTIONS[selectedType] || [];
  const selectedCount = selectedItems.size;

  if (selectedCount === 0) return null;

  const handleAction = async (actionKey: string) => {
    // More specific confirmation messages for different actions
    let confirmMessage = '';
    if (actionKey === 'bulk_cancel') {
      confirmMessage = `Are you sure you want to cancel all scheduled voice agent runs for ${selectedCount} selected ${selectedType}? This cannot be undone.`;
    } else if (actionKey === 'trigger_scheduler') {
      confirmMessage = `Are you sure you want to trigger the scheduler for ${selectedCount} selected ${selectedType}? This may schedule multiple voice agent runs.`;
    } else {
      confirmMessage = `Are you sure you want to ${actionKey.replace('_', ' ')} ${selectedCount} ${selectedType}?`;
    }

    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) return;

    try {
      await onBulkAction(actionKey, Array.from(selectedItems));

      // More specific success messages
      let successMessage = '';
      if (actionKey === 'bulk_cancel') {
        successMessage = `Successfully cancelled scheduled runs for ${selectedCount} ${selectedType}`;
      } else {
        successMessage = `Successfully performed ${actionKey.replace('_', ' ')} on ${selectedCount} ${selectedType}`;
      }

      toast.success(successMessage);
      onClearSelection();
    } catch (error) {
      toast.error(`Failed to ${actionKey.replace('_', ' ')} ${selectedType}`);
    }
  };

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-200">
      <span className="text-sm font-medium text-blue-900">
        {selectedCount} {selectedType} selected
      </span>

      <div className="flex items-center gap-1 ml-4">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.key}
              type="button"
              onClick={() => handleAction(action.key)}
              disabled={isLoading}
              className={`
                flex items-center gap-1 px-2 py-1 text-xs font-medium rounded
                ${action.color} hover:bg-white/50 disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              `}
            >
              <Icon className="h-3 w-3" />
              {action.label}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onClearSelection}
        className="ml-auto text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-white/50"
      >
        Clear selection
      </button>
    </div>
  );
};
